import axios from 'axios';
import { Case, Statement, UploadFile, CheckImage, CaseEntity, EntityMention, EntityRelationship, FileEntityMentionsResponse, AgentRun, AgentRunDetail, CaseChatMessage, CaseBillingResponse } from '../types';
import { CreateAnalysisRequest } from '../constants/constants';
const API_BASE_URL = 'https://in6zkeyaof.execute-api.us-east-1.amazonaws.com/dev';

const createAuthenticatedApi = (token?: string) => {
  return axios.create({
    baseURL: API_BASE_URL,
    headers: token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : {}
  });
};

export const fetchCases = async (
  token?: string,
  options?: { all?: boolean; organization_id?: string }
) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  try {
    let url = '/cases';
    const params = new URLSearchParams();
    if (options?.all) params.append('all', 'true');
    if (options?.organization_id) params.append('organization_id', options.organization_id);
    if ([...params].length > 0) {
      url = `${url}?${params.toString()}`;
    }

    const response = await api.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const createCase = async (caseData: Case, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.post(`${API_BASE_URL}/cases`, caseData);
  return response;
};

// Helper function to upload files with concurrency limit
const uploadFilesWithConcurrencyLimit = async (
  files: UploadFile[],
  case_id: string,
  token: string,
  concurrencyLimit: number = 2,
  onFileProgress?: (fileIndex: number, progress: number) => void,
  onFileError?: (fileIndex: number, error: any) => void
): Promise<any[]> => {
  const results: any[] = [];

  try {
    // Step 1: Get ALL presigned URLs in one API call
    const bulkUrlResponse = await getBulkUploadUrls(
      case_id,
      files.map(f => ({
        filename: f.name,
        fileCategory: f.fileCategory || 'Other',
        fileType: f.type
      })),
      token
    );

    const { uploads } = bulkUrlResponse.data;
    
    // Check for any failed URL generations
    const failedUrls = uploads.filter((u: any) => u.error);
    if (failedUrls.length > 0) {
      console.warn(`${failedUrls.length} file(s) failed to get upload URLs:`, failedUrls);
    }

    // Step 2: Upload files to S3 with concurrency control
    let currentIndex = 0;

    const uploadSingleFile = async (file: UploadFile, idx: number, uploadInfo: any): Promise<any> => {
      if (uploadInfo.error) {
        throw new Error(uploadInfo.error);
      }

      const { uploadUrl, file_id, s3_key } = uploadInfo;
      const MAX_RETRIES = 3;
      const BASE_DELAY = 1000; // 1 second

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          // Upload file directly to S3 with progress and timeout
          await axios.put(uploadUrl, file, {
            headers: { 'Content-Type': file.type },
            timeout: 300000, // 5 minute timeout for large files
            onUploadProgress: (progressEvent) => {
              if (onFileProgress) {
                const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                onFileProgress(idx, percent);
              }
            },
          });

          // Success!
          return {
            file_id,
            filename: file.name,
            fileCategory: file.fileCategory || 'Other',
            s3_key
          };
        } catch (err: any) {
          const isLastAttempt = attempt === MAX_RETRIES;
          const isRetryable = err.code === 'ECONNABORTED' || 
                              err.code === 'ETIMEDOUT' || 
                              err.code === 'ENOTFOUND' ||
                              err.response?.status >= 500;

          if (isLastAttempt || !isRetryable) {
            console.error(`Upload failed for ${file.name} after ${attempt + 1} attempts:`, err.message);
            if (onFileError) onFileError(idx, err);
            throw err;
          }

          // Exponential backoff: 1s, 2s, 4s
          const delay = BASE_DELAY * Math.pow(2, attempt);
          console.warn(`Upload attempt ${attempt + 1} failed for ${file.name}, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      throw new Error('Upload failed after all retries');
    };

    const processNext = async (): Promise<void> => {
      if (currentIndex >= files.length) return;
      
      const currentIdx = currentIndex++;
      try {
        results[currentIdx] = await uploadSingleFile(files[currentIdx], currentIdx, uploads[currentIdx]);
      } catch (err) {
        results[currentIdx] = { error: err };
      }
      await processNext();
    };

    // Start up to `concurrencyLimit` concurrent uploads
    const workers = Array.from(
      { length: Math.min(concurrencyLimit, files.length) }, 
      () => processNext()
    );
    
    await Promise.all(workers);
  } catch (err) {
    console.error('Error in bulk upload:', err);
    throw err;
  }

  return results;
};

export const saveCase = async (
  caseData: Case,
  case_id: string,
  files?: UploadFile[],
  token?: string,
  onFileProgress?: (fileIndex: number, progress: number) => void,
  onFileError?: (fileIndex: number, error: any) => void
) => {
  const api = createAuthenticatedApi(token);

  let uploadedFiles: any[] = [];

  if (files && files.length > 0) {
    // Use concurrency-limited upload instead of Promise.all
    const uploadResults = await uploadFilesWithConcurrencyLimit(
      files,
      case_id,
      token!,
      2, // Limit to 2 concurrent uploads
      onFileProgress,
      onFileError
    );
    
    // Check for failed uploads
    const failedUploads = uploadResults.filter(result => result.error);
    if (failedUploads.length > 0) {
      console.warn(`${failedUploads.length} file(s) failed to upload:`, failedUploads);
      // You could implement retry logic here if needed
    }
    
    // Filter out any failed uploads
    uploadedFiles = uploadResults.filter(result => !result.error);
  }

  // Save case data with file references
  const payload = {
    ...caseData,
    uploadedFiles
  };

  const response = await api.post(`${API_BASE_URL}/case/${case_id}`, payload);
  return response;
};

// Helper function to convert File to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = (reader.result as string).split(',')[1];
      resolve(base64String);
    };
    reader.onerror = reject;
  });
};

export const fetchCaseById = async (case_id: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.get(`/case/${case_id}`);
  return response;
};

export const fetchCaseFiles = async (case_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/files`);
  return response;
};

export const fetchCaseAuditLog = async (case_id: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.get(`/case/${case_id}/audit-log`);
  return response;
};

export const fetchCaseActivityLog = async (case_id: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.get(`/case/${case_id}/activity-log`);
  return response;
};

export const createCaseActivityLogEntry = async (
  case_id: string,
  note_text: string,
  token?: string,
  entry_date?: string
) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.post(`/case/${case_id}/activity-log`, { note_text, entry_date });
  return response;
};

export const updateCaseActivityLogEntry = async (
  case_id: string,
  timestamp: string,
  note_text: string,
  token?: string,
  entry_date?: string
) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.put(`/case/${case_id}/activity-log/${encodeURIComponent(timestamp)}`, { note_text, entry_date });
  return response;
};

export const deleteCaseActivityLogEntry = async (case_id: string, timestamp: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.delete(`/case/${case_id}/activity-log/${encodeURIComponent(timestamp)}`);
  return response;
};

export const fetchCaseBilling = async (case_id: string, token?: string): Promise<CaseBillingResponse> => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.get(`/case/${case_id}/billing`);
  return response.data;
};

export const createCaseBillingEntry = async (
  case_id: string,
  entry: Record<string, unknown> & { entry_type: 'time' | 'payment' },
  token?: string
) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.post(`/case/${case_id}/billing`, entry);
  return response.data;
};

export const updateCaseBillingEntry = async (
  case_id: string,
  entry_id: string,
  entry: Record<string, unknown>,
  token?: string
) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.put(`/case/${case_id}/billing/${encodeURIComponent(entry_id)}`, entry);
  return response.data;
};

export const deleteCaseBillingEntry = async (case_id: string, entry_id: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.delete(`/case/${case_id}/billing/${encodeURIComponent(entry_id)}`);
  return response.data;
};

export const syncCourtDocket = async (case_id: string, token?: string): Promise<{
  message: string;
  new_documents: number;
  new_events: number;
  documents: string[];
  events: string[];
}> => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.post(`/case/${case_id}/court-sync`);
  return response.data;
};

export const generateCaseOverview = async (case_id: string, token?: string): Promise<{ case_overview: string }> => {
  if (!token) {
    throw new Error('No auth token provided');
  }

  const api = createAuthenticatedApi(token);
  const response = await api.post(`/case/${case_id}/generate-overview`);
  return response.data;
};

export const deleteCaseFile = async (case_id: string, file_id: string, token?: string, deleteStatements?: boolean) => {
  const api = createAuthenticatedApi(token);
  const response = await api.delete(`${API_BASE_URL}/case/${case_id}/files/${file_id}`, {
    data: {
      delete_statements: deleteStatements
    }
  });
  return response;
};

export const bulkDeleteCaseFiles = async (case_id: string, file_ids: string[], token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.post(`${API_BASE_URL}/case/${case_id}/files/bulk-delete`, {
    file_ids
  });
  return response;
};

export const fetchCaseAnalyses = async (case_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/analyses`);
  return response;
};

export const fetchProcessedBankStatements = async (case_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/bank-statements`);
  return response;
};

export const fetchProcessedCCStatements = async (case_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/cc-statements`);
  return response;
};

export const fetchProcessedExpenseReports = async (case_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/expense-reports`);
  return response;
};

export interface GlEntriesQuery {
  date_from?: string; // YYYY-MM-DD
  date_to?: string;   // YYYY-MM-DD
  account?: string;   // substring match on account_code/account_name
  limit?: number;
  offset?: number;
}

export const fetchCaseGlEntries = async (case_id: string, query: GlEntriesQuery = {}, token?: string) => {
  const api = createAuthenticatedApi(token);
  const params: Record<string, string> = {};
  if (query.date_from) params.date_from = query.date_from;
  if (query.date_to) params.date_to = query.date_to;
  if (query.account) params.account = query.account;
  if (query.limit != null) params.limit = String(query.limit);
  if (query.offset != null) params.offset = String(query.offset);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/gl-entries`, { params });
  return response;
};

export const createAnalysis = async (analysisData: CreateAnalysisRequest, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.post(`${API_BASE_URL}/case/${analysisData.case_id}/analyses`, analysisData);
  return response;
};

export const deleteAnalysis = async (case_id: string, analysis_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.delete(`${API_BASE_URL}/case/${case_id}/analyses/${analysis_id}`);
  return response;
};

export const fetchAnalysis = async (case_id: string, analysis_id: string, token?: string) => {
  return axios.get(`${API_BASE_URL}/case/${case_id}/analyses/${analysis_id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const fetchCaseUsers = async (case_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/users`);
  return response;
};

export const updateCaseUsers = async (case_id: string, assigned_users: string[], token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.put(`${API_BASE_URL}/case/${case_id}/users`, {
    assigned_users
  });
  return response;
};

export const fetchFileContent = async (case_id: string, file_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/files/${file_id}`);
  return response;
};

export const fetchCheckImages = async (case_id: string, file_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/files/${file_id}/check-images`);
  return response;
};

export const fetchCaseCheckImages = async (case_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/check-images`);
  return response;
};

export const fetchGlEntries = async (case_id: string, file_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/files/${file_id}/gl-entries`);
  return response;
};

export const fetchFinancialStatements = async (case_id: string, file_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/files/${file_id}/financial-statements`);
  return response;
};

export const fetchInvoiceLineItems = async (case_id: string, file_id: string, token?: string) => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/files/${file_id}/invoice-line-items`);
  return response;
};

export interface InvoiceLineItemUpdate {
  vendor?: string;
  entry_date?: string;
  description?: string;
  invoice_number?: string;
  amount?: number;
  mark_reviewed?: boolean;
}

export const updateInvoiceLineItem = async (
  case_id: string, file_id: string, line_item_id: string,
  updates: InvoiceLineItemUpdate, token?: string
) => {
  const api = createAuthenticatedApi(token);
  const response = await api.put(
    `${API_BASE_URL}/case/${case_id}/files/${file_id}/invoice-line-items/${line_item_id}`,
    updates
  );
  return response;
};

export const fetchCaseEntities = async (
  case_id: string,
  token?: string
): Promise<{ entities: CaseEntity[]; total: number }> => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/entities`);
  return response.data;
};

export interface CustomColumnChoice {
  label: string;
  description: string;
}

export interface CustomColumnDefinition {
  definition_id: string;
  organization_id: string;
  name: string;
  column_type: 'llm_classification';
  instructions: string;
  choices: CustomColumnChoice[];
  context_columns: string[];
  created_by: string;
  created_at: string;
}

export const fetchCustomColumnDefinitions = async (token?: string): Promise<{ definitions: CustomColumnDefinition[] }> => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/custom-column-definitions`);
  return response.data;
};

export const createCustomColumnDefinition = async (
  data: { name: string; column_type: 'llm_classification'; instructions: string; choices: CustomColumnChoice[]; context_columns: string[] },
  token?: string
): Promise<{ definition: CustomColumnDefinition }> => {
  const api = createAuthenticatedApi(token);
  const response = await api.post(`${API_BASE_URL}/custom-column-definitions`, data);
  return response.data;
};

export const deleteCustomColumnDefinition = async (definition_id: string, token?: string): Promise<void> => {
  const api = createAuthenticatedApi(token);
  await api.delete(`${API_BASE_URL}/custom-column-definitions/${definition_id}`);
};

export const updateCustomColumnDefinition = async (
  definition_id: string,
  data: Partial<{ name: string; instructions: string; choices: CustomColumnChoice[]; context_columns: string[] }>,
  token?: string
): Promise<{ definition: CustomColumnDefinition }> => {
  const api = createAuthenticatedApi(token);
  const response = await api.put(`${API_BASE_URL}/custom-column-definitions/${definition_id}`, data);
  return response.data;
};

export interface ClassifyRecord {
  record_id: string;
  record_type: string;
  file_id: string;
  context: Record<string, unknown>;
}

// Submits a batch for classification. Returns almost immediately - any
// records already cached come back right away, anything else is kicked off
// as background work server-side (not run synchronously in this request,
// since a real classification call can take 15-30+s, well past what's
// reasonable to hold a browser request open for). Poll
// fetchCustomColumnValues for the records not yet in the returned map.
export const submitCustomColumnClassification = async (
  case_id: string,
  definition_id: string,
  records: ClassifyRecord[],
  token?: string
): Promise<{ values: Record<string, string>; cached_count: number; submitted_count: number; status: 'complete' | 'processing' }> => {
  const api = createAuthenticatedApi(token);
  const response = await api.post(`${API_BASE_URL}/case/${case_id}/custom-columns/${definition_id}/classify`, { records });
  return response.data;
};

// Cheap cache-only read (no LLM calls) - poll this after submitting to find
// out when background classification results are ready.
export const fetchCustomColumnValues = async (
  case_id: string,
  definition_id: string,
  record_ids: string[],
  token?: string
): Promise<{ values: Record<string, string> }> => {
  const api = createAuthenticatedApi(token);
  const response = await api.post(`${API_BASE_URL}/case/${case_id}/custom-columns/${definition_id}/values`, { record_ids });
  return response.data;
};

export const triggerExtractCaseEntities = async (
  case_id: string,
  fullRescan: boolean = false,
  token?: string
): Promise<{ status: string; case_id: string; full_rescan: boolean }> => {
  const api = createAuthenticatedApi(token);
  const response = await api.post(`${API_BASE_URL}/case/${case_id}/extract-entities`, { full_rescan: fullRescan });
  return response.data;
};

export const fetchEntityMentions = async (
  case_id: string,
  entity_id: string,
  token?: string
): Promise<{ entity: CaseEntity; mentions: EntityMention[]; total: number }> => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/entities/${entity_id}/mentions`);
  return response.data;
};

export const fetchFileEntityMentions = async (
  case_id: string,
  file_id: string,
  token?: string
): Promise<FileEntityMentionsResponse> => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/files/${file_id}/entity-mentions`);
  return response.data;
};

export const fetchCaseRelationships = async (
  case_id: string,
  token?: string
): Promise<{ relationships: EntityRelationship[]; entities: CaseEntity[]; total: number }> => {
  const api = createAuthenticatedApi(token);
  const response = await api.get(`${API_BASE_URL}/case/${case_id}/entity-relationships`);
  return response.data;
};

export const getUploadUrl = async (
  case_id: string,
  filename: string,
  fileCategory: string,
  fileType: string,
  token?: string
) => {
  const api = createAuthenticatedApi(token);
  const response = await api.post(`/case/${case_id}/upload-url`, {
    filename,
    fileCategory,
    fileType
  });
  return response;
};

export const getBulkUploadUrls = async (
  case_id: string,
  files: Array<{ filename: string; fileCategory: string; fileType: string }>,
  token?: string
) => {
  const api = createAuthenticatedApi(token);
  const response = await api.post(`/case/${case_id}/upload-urls`, {
    files
  });
  return response;
};

export async function getStatementProcessingDetails(case_id: string, file_id: string, token?: string) {
    if (!token) {
        throw new Error('No auth token provided');
    }

    console.log('Fetching processing details:', { case_id, file_id });
    const api = createAuthenticatedApi(token);
    try {
        const response = await api.get(`${API_BASE_URL}/case/${case_id}/file/${file_id}/processing-details`);
        console.log('Processing details response:', response.status, response.data);
        return response;
    } catch (error: any) {
        console.error('Error getting statement processing details:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        });
        throw error;
    }
}

export const updateStatementData = async (
    case_id: string,
    file_id: string,
    statement_data: Statement[],
    token?: string
) => {
    const api = createAuthenticatedApi(token);
    const response = await api.put(
        `${API_BASE_URL}/case/${case_id}/files/${file_id}/statement-data`,
        { statement_data }
    );
    return response;
};

export const deleteStatement = async (
    case_id: string,
    file_id: string,
    statement_id: string,
    token?: string
) => {
    const api = createAuthenticatedApi(token);
    const response = await api.delete(
        `${API_BASE_URL}/case/${case_id}/files/${file_id}/statement-data/${statement_id}`
    );
    return response;
};

export const updateCheckImageData = async (
    case_id: string,
    file_id: string,
    check_image_id: string,
    check_image_data: CheckImage,
    token?: string
) => {
    const api = createAuthenticatedApi(token);
    const response = await api.put(
        `${API_BASE_URL}/case/${case_id}/files/${file_id}/check-images/${check_image_id}`,
        check_image_data
    );
    return response;
};

export const deleteCheckImage = async (
    case_id: string,
    file_id: string,
    check_image_id: string,
    token?: string
) => {
    const api = createAuthenticatedApi(token);
    const response = await api.delete(
        `${API_BASE_URL}/case/${case_id}/files/${file_id}/check-images/${check_image_id}`
    );
    return response;
};

// Required Files API functions
import { RequiredFile, CreateRequiredFileRequest, UpdateRequiredFileRequest } from '../types';

export const fetchRequiredFiles = async (case_id: string, token?: string) => {
    if (!token) {
        throw new Error('No auth token provided');
    }
    const api = createAuthenticatedApi(token);
    const response = await api.get(`${API_BASE_URL}/case/${case_id}/required-files`);
    return response;
};

export const createRequiredFile = async (requirementData: CreateRequiredFileRequest, token?: string) => {
    if (!token) {
        throw new Error('No auth token provided');
    }
    const api = createAuthenticatedApi(token);
    const response = await api.post(`${API_BASE_URL}/case/${requirementData.case_id}/required-files`, requirementData);
    return response;
};

export const updateRequiredFile = async (
    case_id: string,
    requirement_id: string,
    updateData: UpdateRequiredFileRequest,
    token?: string
) => {
    if (!token) {
        throw new Error('No auth token provided');
    }
    const api = createAuthenticatedApi(token);
    const response = await api.put(`${API_BASE_URL}/case/${case_id}/required-files/${requirement_id}`, updateData);
    return response;
};

export const deleteRequiredFile = async (case_id: string, requirement_id: string, token?: string) => {
    if (!token) {
        throw new Error('No auth token provided');
    }
    const api = createAuthenticatedApi(token);
    const response = await api.delete(`${API_BASE_URL}/case/${case_id}/required-files/${requirement_id}`);
    return response;
};

export const fetchStatementGaps = async (
    case_id: string,
    token?: string
): Promise<{ accounts: any[]; summary: any }> => {
    if (!token) {
        throw new Error('No auth token provided');
    }
    const api = createAuthenticatedApi(token);
    const response = await api.get(`${API_BASE_URL}/case/${case_id}/statement-gaps`);
    return response.data;
};

export const manuallyMatchFile = async (case_id: string, file_id: string, requirement_id: string, month?: string, token?: string) => {
    if (!token) {
        throw new Error('No auth token provided');
    }
    const api = createAuthenticatedApi(token);
    const body = month ? { month } : {};
    return api.post(`${API_BASE_URL}/case/${case_id}/files/${file_id}/match/${requirement_id}`, body);
};

export const manuallyUnmatchFile = async (case_id: string, file_id: string, requirement_id: string, month?: string, token?: string) => {
    if (!token) {
        throw new Error('No auth token provided');
    }
    const api = createAuthenticatedApi(token);
    const url = `${API_BASE_URL}/case/${case_id}/files/${file_id}/match/${requirement_id}`;
    if (month) {
        return api.delete(url, { data: { month } });
    }
    return api.delete(url);
};

export const updateFileMetadata = async (
    case_id: string,
    file_id: string,
    updates: { file_type?: string; classification_status?: string; classification_method?: string; page_rotations?: Record<string, number> },
    token?: string
) => {
    if (!token) {
        throw new Error('No auth token provided');
    }
    const api = createAuthenticatedApi(token);
    const response = await api.put(`${API_BASE_URL}/case/${case_id}/files/${file_id}/metadata`, updates);
    return response;
};

// -------------------------------------------------------------------------
// Agent API
// -------------------------------------------------------------------------

export const listAgentRuns = async (case_id: string, token?: string): Promise<{ runs: AgentRun[] }> => {
    const api = createAuthenticatedApi(token);
    const response = await api.get(`${API_BASE_URL}/cases/${case_id}/agent/runs`);
    return response.data;
};

export const getAgentRunDetail = async (case_id: string, run_id: string, token?: string): Promise<AgentRunDetail> => {
    const api = createAuthenticatedApi(token);
    const response = await api.get(`${API_BASE_URL}/cases/${case_id}/agent/runs/${run_id}`);
    return response.data;
};

export const createAgentRun = async (
    case_id: string,
    data: { objective: string; case_type?: string; max_steps?: number },
    token?: string
): Promise<{ run_id: string; status: string; execution_arn: string }> => {
    const api = createAuthenticatedApi(token);
    const response = await api.post(`${API_BASE_URL}/cases/${case_id}/agent/runs`, data);
    return response.data;
};

export const controlAgentRun = async (
    case_id: string,
    run_id: string,
    action: 'stop' | 'resume',
    feedback?: string,
    token?: string
): Promise<{ run_id: string; action: string; status: string }> => {
    const api = createAuthenticatedApi(token);
    const response = await api.post(`${API_BASE_URL}/cases/${case_id}/agent/runs/${run_id}/control`, {
        action,
        ...(feedback ? { feedback } : {}),
    });
    return response.data;
};

// -------------------------------------------------------------------------
// Case Chat
// -------------------------------------------------------------------------

export const fetchCaseChatHistory = async (case_id: string, token?: string): Promise<{ messages: CaseChatMessage[] }> => {
    const api = createAuthenticatedApi(token);
    const response = await api.get(`${API_BASE_URL}/case/${case_id}/chat`);
    return response.data;
};

export const sendCaseChatMessage = async (case_id: string, message: string, token?: string): Promise<{ message: string }> => {
    const api = createAuthenticatedApi(token);
    const response = await api.post(`${API_BASE_URL}/case/${case_id}/chat`, { message });
    return response.data;
};
